<template>
  <apexchart
    type="rangeBar"
    height="150"
    :options="chartOptions"
    :series="plotSeries"
    class="gvnts-plot"
  />
</template>

<script>
/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { isPlayerGroupQA             } from '../model/event.js';
import { getPlayerGroupName          } from '../model/event.js';
import { getPlayerGroupPublicity     } from '../model/event.js';
import { getPlayerGroupPublicityName } from '../model/event.js';

import { getGvntTypeName              } from '../model/gvnt.js';
import { isGvntStatusActive           } from '../model/gvnt.js';
import { isGvntStatusFinished         } from '../model/gvnt.js';
import { isGvntStatusClosed           } from '../model/gvnt.js';
import { getGvntPlannedDurationStr    } from '../model/gvnt.js';
import { getGvntRealDurationStrSimple } from '../model/gvnt.js';

import { getMomentMinStr                    } from '../util.js';
import { getMomentMinStrOptionalWithDefault } from '../util.js';

import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'GvntsPlot',

  components: {
    apexchart: VueApexCharts
  },

  props: {
    gvnts: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      // Fix width error.
      // https://github.com/apexcharts/apexcharts.js/issues/1077
      canShowData: false,

      selectedGvntId: null,
    };
  },

  computed: {
    /** @returns {Object} */
    chartOptions() {
      if(!this.canShowData) {
        return {};
      }

      const now = new Date();
      const nowMsec = now.getTime();

      return {
        chart: {
          type: 'rangeBar',

          toolbar: {
            offsetY: -12,
          },

          animations: {
            enabled: false
          },
          events: {
            dataPointSelection: this.handleDataPointSelection,
            click: this.handleDataPointMouseEnter,
          },
        },

        annotations: {
          xaxis: [
            {
              x: nowMsec,
              borderColor: '#00C853',
              label: {
                style: {
                  color: '#00C853',
                },
                text: 'now',
                orientation: 'horizontal',
                offsetY: -8,
              }
            }
          ]
        },

        plotOptions: {
          bar: {
            horizontal: true
          }
        },

        xaxis: {
          type: 'datetime',

          labels: {
            datetimeUTC: false,
            datetimeFormatter: {
              day: 'MMM d',
            },
          },
        },

        /*
        dataLabels: {
          enabled: true,
          formatter(val, opts) {
            const seriesIndex = opts.seriesIndex;
            const dataPointIndex = opts.dataPointIndex;

            const series = opts.w.config.series;

            const dataPoint = series[seriesIndex].data[dataPointIndex];
            const gvnt = dataPoint.gvnt;

            const gvntTypeName = getGvntTypeName(gvnt.type);
            return gvntTypeName;
          },
        },
        */

        grid: {
          padding: {
            left: 0
          }
        },

        tooltip: {
          custom: createTooltip,
        },
      };
    },

    /** @returns {Object[]} */
    plotSeries() {
      if(!this.canShowData) {
        return [];
      }

      const data = [];

      for(const gvnt of this.gvnts) {
        const publicity = getPlayerGroupPublicity(gvnt.playerGroupId);
        const lineName = getPlayerGroupPublicityName(publicity);

        const gvntData = {
          x: lineName,
          y: [
            getGnvtBegin(gvnt) * 1000,
            getGvntEnd(gvnt)   * 1000
          ],
          gvnt: gvnt
        };

        if(isGvntStatusActive(gvnt.status)) {
          gvntData.fillColor = '#00E396'; // зеленый
        }
        if(isGvntStatusClosed(gvnt.status)) {
          gvntData.fillColor = '#bdbdbd'; // серый
        }

        data.push(gvntData);
      }

      data.sort(comparePlotItems);

      const series = [
        {data: data},
      ];
      return series;
    },
  },

  created() {
    this.$nextTick(() => {
      this.canShowData = true;
    });
  },

  methods: {
    handleDataPointSelection(event, chartContext, config) {
      const seriesIndex = config.seriesIndex;
      const dataPointIndex = config.dataPointIndex;

      const series = config.w.config.series;

      const dataPoint = series[seriesIndex].data[dataPointIndex];
      const gvnt = dataPoint.gvnt;

      this.selectedGvntId = gvnt.id;
    },

    handleDataPointMouseEnter() {
      const selectedGvntId = this.selectedGvntId;
      if(selectedGvntId !== null) {
        this.$emit('gvnt-clicked', selectedGvntId);
      }
    },
  },
};

//-----------------------------------------------------------------------------
// Запланированные данные:
//   - запланированное время старта (stime)
//   - запланированное время завершения (ftime)
// Запланированные данные есть всегда,
// т.к. они инициализируются при создании игрового события.
//
// Реальные данные:
//   - реальное время старта (srtime)
//   - реальное время завершения (frtime)
// Реальные данные есть не всегда.
// Реальное время старта (srtime) становится известно после старта игрового события.
// Реальное время завершения (frtime) становится известно после завершения игрового события.
//
// Если реальных данных еще нет (а есть только запланированные),
// то нужно показывать на графике запланированные данные.
//
// Если уже есть реальные данные (а не только запланированные),
// то нужно показывать на графике реальные данные (а не запланированные).

function getGnvtBegin(gvnt) {
  const s = gvnt.status;

  if(isGvntStatusActive(s) || isGvntStatusFinished(s) || isGvntStatusClosed(s)) {
    // реальные данные известны
    return gvnt.srtime;
  }

  return gvnt.stime;
}

function getGvntEnd(gvnt) {
  const s = gvnt.status;

  if(isGvntStatusFinished(s) || isGvntStatusClosed(s)) {
    // реальные данные известны
    return gvnt.frtime;
  }

  return gvnt.ftime;
}

//-----------------------------------------------------------------------------

function comparePlotItems(dataA, dataB) {
  const groupA = dataA.gvnt.playerGroupId;
  const groupB = dataB.gvnt.playerGroupId;

  if(groupA === groupB) {
    return 0;
  }

  return isPlayerGroupQA(groupA) ? -1 : 1;
}

//-----------------------------------------------------------------------------

function createTooltip(param) {
  const seriesIndex = param.seriesIndex;
  const dataPointIndex = param.dataPointIndex;

  const sObject = param.w.config.series[seriesIndex];
  const dataObject = sObject.data[dataPointIndex];
  const gvnt = dataObject.gvnt;

  const gvntIdStr = gvnt.id.toString();
  const gvntTypeName = getGvntTypeName(gvnt.type);
  const playerGroupName = getPlayerGroupName(gvnt.playerGroupId);

  const stimeStr = getMomentMinStr(gvnt.stime);
  const ftimeStr = getMomentMinStr(gvnt.ftime);
  const plannedDurationStr = getGvntPlannedDurationStr(gvnt);

  const srtimeStr = getMomentMinStrOptionalWithDefault(gvnt.srtime, '---');
  const frtimeStr = getMomentMinStrOptionalWithDefault(gvnt.frtime, '---');
  const realDurationStr = getGvntRealDurationStrSimple(gvnt);

  let code = '<div class="gvnts-plot-tooltip">';
  code += `<span class="header">Gvnt ${gvntIdStr}. ${gvntTypeName}</span>`;
  code += `<p class="mb-1">Segment: ${playerGroupName}</p>`

  code += '<table>';
  code +=   '<tr>';
  code +=     '<td></td>';
  code +=     '<th class="h">begin</th>';
  code +=     '<th class="h">end</th>';
  code +=     '<th class="h">duration</th>';
  code +=   '</tr>';
  code +=   '<tr>';
  code +=     '<th>plan</th>';
  code +=     `<td>${stimeStr}</td>`;
  code +=     `<td>${ftimeStr}</td>`;
  code +=     `<td>${plannedDurationStr}</td>`;
  code +=   '</tr>';

  if(gvnt.srtime > 0) {
    code +=   '<tr>';
    code +=     '<th>real</th>';
    code +=     `<td>${srtimeStr}</td>`;
    code +=     `<td>${frtimeStr}</td>`;
    code +=     `<td>${realDurationStr}</td>`;
    code +=   '</tr>';
  }

  code += '</table>';
  code += '</div>';

  code += '</div>';

  return code;
}
</script>

<style>
.gvnts-plot .apexcharts-tooltip.apexcharts-theme-light {
  background: rgba(255, 255, 255, 0.8);
}

.gvnts-plot-tooltip {
  padding: 0.3rem;
}

.gvnts-plot-tooltip .header {
  font-weight: bold;
}

.gvnts-plot-tooltip th.h,
.gvnts-plot-tooltip td {
  padding-left: .5rem;
}

/**
  При наведении на прямоугольник (который обозначает проведение игрового события)
  курсор должен принимать форму руки с указательным пальцем.
*/
.gvnts-plot .apexcharts-rangebar-area {
  cursor: pointer;
}
</style>
